/**
 * @fileoverview Form Schemas - Zod validation schemas for ingestion step forms.
 *
 * Each step that has form validation uses schemas defined here.
 * Schemas are designed to be permissive (mostly optional fields) since
 * the goal is to save whatever data is available.
 *
 * Steps:
 *   0. Basics - Firm + Name + Case Type (creates matter)
 *   1. Case Info - Attorney, Billing, DOL, etc.
 *   2. Client Information - Contact details, address
 *   3. Liability/Police Report - Police report details
 *   4. Treatment - Medical providers (no form schema needed)
 *   5. Coverage - Insurance coverages (no form schema needed)
 *   6. Documents - File uploads (no form schema needed)
 *   7. Notes (Case Summary) - Intake and general notes
 *   8. Review - Summary only (no form schema needed)
 */
import { z } from 'zod';

import {
  MatterType,
  SimplifiedBillingCode,
} from '@/services/types/client-intake-types';
import { Gender, PREFERRED_LANGUAGE_OPTIONS } from '@/services/types/matter-client-types';
import { isPhoneNumberValid } from '@/utils/validation';

// ============================================================================
// Shared/Utility Schemas
// ============================================================================

/**
 * Phone number validation schema.
 * Accepts:
 * - Empty strings (will be filtered out during save)
 * - E.164 format: +1XXXXXXXXXX
 * - US formats: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXXXXXXXXX
 */
const phoneNumberSchema = z.object({
  id: z.string().optional(),
  phone_number: z.string().refine(
    (val) => {
      // Allow empty - we filter out empty values during save
      if (!val || val.trim() === '') return true;
      return isPhoneNumberValid(val);
    },
    { message: 'Invalid phone number format (e.g. 323-334-5768 or +13233345768)' },
  ),
});

// ============================================================================
// Step 0: Basics (Creation Phase)
// ============================================================================

/** Default value for the "Hide tasks during initial review" checkbox on new matters. */
export const GATE_TASKS_DEFAULT = false;

/**
 * Combined schema for guided matter creation (Steps 0-1).
 * Step 0 uses the required fields, Step 1 uses the optional fields.
 */
export const createMatterSchema = z.object({
  // Step 0: Basics (required fields)
  firm: z.object({
    id: z.string().min(1, 'Firm is required'),
    name: z.string().min(1, 'Firm name is required'),
  }),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  matter_type: z.nativeEnum(MatterType, { errorMap: () => ({ message: 'Case type is required' }) }),
  gate_tasks_during_initial_review: z.boolean().optional().default(GATE_TASKS_DEFAULT),

  // Step 1: Case Info (optional fields)
  representing_attorney: z.string().nullable().optional(),
  contact_client_directly: z.boolean(),
  simplified_billing_code: z.nativeEnum(SimplifiedBillingCode).nullable().optional(),
  case_manager_id: z.string().nullable().optional(),
  auto_task_assignment: z.boolean().nullable().optional(),
  date_of_loss: z.string().nullable().optional(),
  statute_of_limitations_date: z.string().nullable().optional(),
  incident_location: z.string().optional(),
  incident_summary: z.string().optional(),
  external_cms_url: z
    .string()
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      { message: 'URL must start with http:// or https://' }
    )
    .nullable()
    .optional()
    .or(z.literal('')),
  external_drive_url: z
    .string()
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      { message: 'URL must start with http:// or https://' }
    )
    .nullable()
    .optional()
    .or(z.literal('')),
  cms_email: z.string().email().nullable().optional().or(z.literal('')),
  case_id: z.string().nullable().optional(),
});

/** Step 0 (Basics) validation - all required fields */
export const basicsStepSchema = createMatterSchema.pick({
  firm: true,
  first_name: true,
  last_name: true,
  matter_type: true,
  gate_tasks_during_initial_review: true,
});

// ============================================================================
// Step 1: Case Info (Creation Phase)
// ============================================================================

/** Step 1 (Case Info) validation - all optional */
export const caseInfoStepSchema = createMatterSchema.omit({
  firm: true,
  first_name: true,
  last_name: true,
  matter_type: true,
});

// ============================================================================
// Step 2: Client Information (Ingestion Phase)
// ============================================================================

/**
 * Client Information - all fields optional (save whatever is present).
 * Note: first_name and last_name are managed in Basics step, not here.
 */
export const clientInformationSchema = z.object({
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  date_of_birth: z.string().optional(),
  gender: z
    .enum([Gender.MALE, Gender.FEMALE, Gender.OTHER])
    .optional()
    .nullable()
    .or(z.literal('')),
  ssn: z.string().optional(),
  last_four_of_ssn: z.string().max(4).optional(),
  preferred_language: z
    .enum(PREFERRED_LANGUAGE_OPTIONS.map((o) => o.value) as [string, ...string[]])
    .optional()
    .nullable()
    .or(z.literal('')),
  phone_numbers: z.array(phoneNumberSchema).optional(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postcode: z.string().optional(),
    })
    .optional(),
});

export type ClientInformationFormData = z.infer<typeof clientInformationSchema>;

// ============================================================================
// Step 3: Liability/Police Report (Ingestion Phase - Audit Tracked)
// ============================================================================

export const liabilityPoliceReportSchema = z.object({
  filing_status: z.enum(['Filed', 'Not Filed', 'Unknown']).optional(),
  report_identifier: z.string().optional(),
  department_name: z.string().optional(),
  report_file: z.instanceof(File).optional().nullable(),
});

export type LiabilityPoliceReportFormData = z.infer<typeof liabilityPoliceReportSchema>;

// ============================================================================
// Step 4: Treatment (Ingestion Phase - Audit Tracked)
// No form schema needed - uses provider search component
// ============================================================================

// ============================================================================
// Step 5: Coverage (Ingestion Phase - Audit Tracked)
// No form schema needed - uses coverage entry cards with inline forms
// ============================================================================

// ============================================================================
// Step 6: Documents (Ingestion Phase)
// No form schema needed - uses file upload component
// ============================================================================

// ============================================================================
// Step 7: Notes / Case Summary (Ingestion Phase)
// ============================================================================

export const caseSummarySchema = z.object({
  intake_notes: z.string().optional(),
  general_notes: z.string().optional(),
});

export type CaseSummaryFormData = z.infer<typeof caseSummarySchema>;

// ============================================================================
// Step 8: Review (Ingestion Phase)
// No form schema needed - summary display only
// ============================================================================
